import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Search, Sparkles, QrCode, Layers, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1758548157195-67d141468467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwd29vZCUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MTA3MDY4OHww&ixlib=rb-4.1.0&q=85";
const CRAFT = "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwbHl3b29kJTIwY3JhZnRzbWFuc2hpcCUyMGNhcnBlbnRyeXxlbnwwfHx8fDE3ODEwNzA2ODh8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-12 pt-8 md:px-8 md:pb-20 md:pt-16">
        <div className="grid items-center gap-8 md:gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="animate-fade-up lg:col-span-7">
            <div className="hed-overline">Heda Plywoods · AI product knowledge</div>
            <h1 className="mt-3 font-display text-4xl font-bold leading-[1.05] text-heda-ink sm:text-5xl md:text-6xl">
              Every grain.
              <br />
              <span className="text-heda-gold">Every spec.</span>
              <br />
              On every phone.
            </h1>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-heda-sub md:text-lg">
              Scan a Heda QR. Get the entire product — dimensions, finishes, installation,
              FAQs — and an AI assistant that answers, instantly. No PDFs to chase.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/search" data-testid="hero-search-cta" className="heda-btn-primary">
                <Search size={16} /> Browse the catalog
              </Link>
              <Link to="/admin/login" data-testid="hero-admin-cta" className="hed-btn-line px-6">
                Admin studio <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="animate-fade-up lg:col-span-5">
            <div className="relative">
              <img
                src={HERO}
                alt="Premium wood interior"
                className="aspect-[4/5] w-full rounded-3xl border border-heda-line object-cover shadow-heda"
              />
              <div className="heda-card absolute -bottom-6 -left-6 hidden max-w-[220px] p-5 shadow-heda sm:block">
                <div className="hed-dot mb-3" />
                <div className="font-display text-lg font-bold leading-tight text-heda-ink">
                  &ldquo;What thickness is this for kitchens?&rdquo;
                </div>
                <div className="mt-2 text-[11.5px] text-heda-mute">
                  — Real customer question, answered by Heda AI in 1.2s.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-heda-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-20">
          <div className="hed-overline">The pipeline</div>
          <h2 className="mt-2 max-w-2xl font-display text-2xl font-bold leading-tight text-heda-ink md:text-[30px]">
            From a stack of brochures to a live product page — in minutes.
          </h2>
          <div className="hed-stagger mt-10 grid gap-4 md:grid-cols-3">
            {[
              { i: "01", title: "Upload anything", icon: Layers, body: "PDF brochures, catalog scans, technical sheets, product photos." },
              { i: "02", title: "AI structures it", icon: Sparkles, body: "Hedaply extracts name, code, specs, sizes, finishes — every detail." },
              { i: "03", title: "Publish + QR + chat", icon: QrCode, body: "A premium product page goes live. Every print can carry a QR code linking to an AI assistant trained on that exact product." },
            ].map(({ i, title, icon: Icon, body }) => (
              <div key={i} className="heda-card p-5 md:p-6">
                <div className="font-display text-4xl font-bold tabular-nums text-heda-gold">{i}</div>
                <div className="mt-3 flex items-center gap-2">
                  <Icon size={16} className="text-heda-gold" />
                  <h3 className="font-display text-[18px] font-bold text-heda-ink">{title}</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-heda-sub">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <img src={CRAFT} alt="Craft" className="aspect-[4/3] w-full rounded-3xl border border-heda-line object-cover shadow-heda" />
          <div>
            <div className="hed-overline">Built for thousands of SKUs</div>
            <h2 className="mt-2 font-display text-2xl font-bold leading-tight text-heda-ink md:text-[30px]">
              You make great plywood.
              <br />
              <span className="text-heda-olive">We make it findable.</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-heda-sub md:text-base">
              Hedaply replaces hours of data entry with minutes of approval. AI does the reading.
              Your team does the deciding.
            </p>
            <Link
              to="/admin/login"
              className="heda-btn mt-6 px-6"
              data-testid="footer-admin-cta"
            >
              Enter the studio <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
